import {
    Autocomplete,
    Button,
    Checkbox,
    createFilterOptions,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
} from '@mui/material';
import clsx from 'clsx';
import { CheckboxWithLabel } from 'formik-mui';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdArrowDropDown, MdCheck, MdCheckBox, MdCheckBoxOutlineBlank, MdDelete, MdRefresh } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import Loader from 'GeminiViewerComponent/components/Loader';
import { MultiSelectDoneButton } from 'GeminiViewerComponent/components/MultiSelectDoneButton';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { autoCompleteStyles } from 'GeminiViewerComponent/components/styles';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { AssetTypes } from 'GeminiViewerComponent/helpers';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { deleteKey } from 'GeminiViewerComponent/helpers/cacheStore';
import { sortByCaseInsensitiveText } from 'GeminiViewerComponent/helpers/lodashUtils';
import { FormTextField } from 'components/TextField';
import { fetchGroups, getGroupStatus, resetGroupState, selectAllGroups } from 'features/groups/groupsSlice';
import { selectTags } from 'features/tags/tagsSlice';
import { checkUniqueTagName, checkUniqueZoneCategory } from 'helpers/checkUnique';
import { makeFormStyles } from '../../../forms/styles';

const icon = <MdCheckBoxOutlineBlank className="react-icon" fontSize="small" />;
const checkedIcon = <MdCheckBox className="react-icon" fontSize="small" />;
const filter = createFilterOptions();

const SelectionAutocomplete = ({
    title,
    keyProperty,
    nameProperty,
    entityIds,
    setEntityIds,
    entitySelector,
    entityStatusSelector,
    fetchEntityPage,
    fetchParams,
    formField,
    placeholder,
    multiSelection = true,
    errorMessage,
    touched,
    enableCreatable = false,
    creatableDialogTitle = '',
    creatableDialogFieldLabel = '',
    creatableDispatch,
    creatableParams = null,
    enableCache = false,
    refreshCacheType = '',
    refreshCacheTitle = '',
    extraOptions = [],
    readOnly = false,
    entitySelectorArgs = null,
    handleItemTagEdit = () => {},
    showCloseItemEdit = false,
    cancelItemTagEdit = () => {},
    disableClearable = false,
    size = '',
    showInitialValuesAlways = false,
    onCustomChange = function () {},
    fieldValidationLength = null,
    fieldValidation = false,
    freeSolo = true,
    disablePortal = false,
}) => {
    const DIALOG_INITIAL_STATE = useMemo(
        () => ({
            isDialogOpen: false,
            [nameProperty]: '',
        }),
        [nameProperty]
    );

    const theme = useSelector(selectDefaultTheme);
    const { enqueueSnackbar } = useSnackbar();
    const formClasses = makeFormStyles(theme);
    const classes = autoCompleteStyles(theme);
    const dispatch = useDispatch();
    let entities = useSelector((state) => entitySelector(state, entitySelectorArgs));
    entities = [...extraOptions, ...entities];
    const entityStatus = useSelector((state) => entityStatusSelector(state));
    const entitiesByIdMap = Object.create(null);
    const [isLoading, setIsLoading] = useState(false);
    const [manageDialog, setManageDialog] = useState(DIALOG_INITIAL_STATE);
    const [errors, setErrors] = useState(null);
    const [limitCategory, setLimitCategory] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const tags = useSelector(selectTags);

    const validateField = (value) => {
        if (!value) {
            setErrors('Required');
        } else {
            setErrors(null);
            if (
                creatableDialogTitle === 'category' ||
                creatableDialogTitle === 'zone category' ||
                creatableDialogTitle === 'tool category'
            ) {
                if (fieldValidationLength && value.length > fieldValidationLength) {
                    setErrors(`Must be ${fieldValidationLength} characters or less`);
                } else if (checkUniqueZoneCategory(entities, value)) {
                    setErrors(`Must be unique ${creatableDialogTitle} name`);
                }
            }
            if (creatableDialogTitle === 'tag') {
                if (fieldValidationLength && value.length > fieldValidationLength) {
                    setErrors(`Must be ${fieldValidationLength} characters or less`);
                } else if (checkUniqueTagName(tags, value)) {
                    setErrors('Must be unique tag name');
                }
            }
        }
    };
    useEffect(() => {
        validateField(manageDialog[nameProperty]);
        return () => {
            setLimitCategory(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manageDialog]);

    entities.forEach((row) => {
        if (!entitiesByIdMap[row[keyProperty]]) {
            entitiesByIdMap[row[keyProperty]] = row;
        }
    });

    const getDefaultValue = () => {
        if (multiSelection) {
            const selectedValue = [];
            entityIds?.forEach((item) => {
                if (entitiesByIdMap[item]) {
                    selectedValue.push(entitiesByIdMap[item]);
                }
            });
            return selectedValue || [];
        } else {
            return entitiesByIdMap[entityIds] || '';
        }
    };

    const isNewValue = (newValue) => {
        return entities.find((e) => e[nameProperty] == newValue) == null;
    };

    const handleChange = (newValue) => {
        if (!multiSelection) {
            if (enableCreatable) {
                const updatedValue =
                    typeof newValue === 'string'
                        ? newValue
                        : newValue && newValue.inputValue
                        ? newValue.inputValue
                        : null;

                if (updatedValue) {
                    // Make sure value doesn't already exists
                    if (isNewValue(updatedValue)) {
                        // timeout to avoid instant validation of the dialog's form.
                        setTimeout(() => {
                            setManageDialog({
                                [nameProperty]: updatedValue,
                                keyProperty: keyProperty,
                                isDialogOpen: true,
                            });
                        }, 250);
                    }
                }
            }
            setEntityIds(formField, newValue ? newValue[keyProperty] : '');
        } else {
            if (enableCreatable) {
                const newValueObj = newValue[newValue.length - 1];
                const updatedValue =
                    typeof newValueObj === 'string'
                        ? newValueObj
                        : newValueObj && newValueObj.inputValue
                        ? newValueObj.inputValue
                        : null;

                if (updatedValue) {
                    if (isNewValue(updatedValue)) {
                        // timeout to avoid instant validation of the dialog's form.
                        setTimeout(() => {
                            setManageDialog({
                                [nameProperty]: updatedValue,
                                keyProperty: keyProperty,
                                isDialogOpen: true,
                            });
                        }, 250);
                    }
                }
            }
            setEntityIds(
                formField,
                newValue
                    ?.filter((item) => item[keyProperty] != undefined)
                    .map((item) => (item ? item[keyProperty] : null))
            );
        }
    };

    const onRefreshIconClick = () => {
        deleteKey(refreshCacheType);
        dispatch(fetchEntityPage({ ...fetchParams }));
    };

    const filterOption = (options, params) => {
        const filtered = filter(options, params);
        if (enableCreatable && params?.inputValue !== '') {
            const isExisting = options.some((option) => params?.inputValue === option[nameProperty]);
            if (params?.inputValue !== '' && !isExisting) {
                filtered.push({
                    inputValue: params.inputValue,
                    [nameProperty]: `Add "${params.inputValue}"`,
                    [keyProperty]: -1,
                });
            }
        }
        return filtered;
    };

    const handleSubmit = useCallback(async () => {
        if (isFormSubmitted === true) {
            return;
        }
        setIsFormSubmitted(true);
        let result = null;
        if (creatableDialogTitle === 'category') {
            result = await dispatch(
                creatableDispatch(
                    creatableParams && limitCategory ? { ...manageDialog, ...creatableParams } : manageDialog
                )
            );
        } else if (creatableDialogTitle === 'tag') {
            result = await dispatch(creatableDispatch({ ...manageDialog, ...creatableParams }));
        } else {
            result = await dispatch(
                creatableDispatch(creatableParams ? { ...manageDialog, ...creatableParams } : manageDialog)
            );
        }
        if (!result.error) {
            const newId = result.payload[keyProperty];
            const updatedIds = multiSelection ? [...entityIds.filter((id) => id !== undefined), newId] : newId;
            setEntityIds(formField, updatedIds);
            if (multiSelection) {
                let newValues = entities.filter((e) => updatedIds.includes(e[keyProperty]));
                newValues.push(result.payload);
                onCustomChange(newValues);
            } else {
                onCustomChange(newId);
            }
        } else {
            enqueueSnackbar(result?.error?.message, {
                action: (key) => <SnackbarDismiss snackItem={key} />,
                variant: 'error',
            });
            return;
        }
        dispatch(resetGroupState());
        setIsFormSubmitted(false);
        setManageDialog(DIALOG_INITIAL_STATE);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        DIALOG_INITIAL_STATE,
        creatableDialogTitle,
        creatableDispatch,
        creatableParams,
        dispatch,
        enqueueSnackbar,
        entityIds,
        formField,
        isFormSubmitted,
        keyProperty,
        limitCategory,
        manageDialog,
        multiSelection,
        setEntityIds,
    ]);

    useEffect(() => {
        if (entityStatus === LoadingStatus.Idle) {
            setIsLoading(true);
            dispatch(fetchEntityPage({ ...fetchParams }));
        }
        if (entityStatus === LoadingStatus.Loaded) {
            setIsLoading(false);
        }
    }, [entityStatus, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDialogKeyUp = (event) => {
        if (manageDialog?.isDialogOpen && event.key === 'Enter') {
            handleSubmit();
        }
    };

    useEffect(() => {}, [manageDialog]);

    if (isLoading)
        return (
            <Loader
                styles={{
                    width: '100%',
                    marginTop: '8px',
                    marginBottom: '4px',
                    height: '56px',
                }}
                enableBorder
            />
        );

    return (
        <>
            <div className={`${classes.root}`}>
                <Autocomplete
                    size={size}
                    popupIcon={<MdArrowDropDown />}
                    forcePopupIcon
                    multiple={multiSelection}
                    disablePortal={disablePortal}
                    readOnly={readOnly}
                    limitTags={2}
                    name={formField}
                    clearIcon={<MdDelete fontSize="18" />}
                    onChange={(_, newValue) => {
                        if (showInitialValuesAlways === false) {
                            handleChange(newValue);
                            onCustomChange(newValue);
                        }
                    }}
                    disableClearable={showCloseItemEdit || disableClearable}
                    options={entities.sort(sortByCaseInsensitiveText(nameProperty))}
                    disableCloseOnSelect={multiSelection}
                    getOptionLabel={(option) => {
                        // Value selected with enter, right from the input
                        if (typeof option === 'string') {
                            return option;
                        }
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        // Regular option
                        return option[nameProperty];
                    }}
                    value={getDefaultValue()}
                    renderOption={(props, option, { selected }) => (
                        <li {...props} key={option[keyProperty]}>
                            {multiSelection && (
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                            )}
                            {option[nameProperty] !== '' ? option[nameProperty] : '-'}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={fieldValidation}
                            label={title}
                            className={clsx([
                                errorMessage && touched ? classes.errorField : '',
                                enableCache && showCloseItemEdit === false ? classes.clearIndicator : '',
                            ])}
                            placeholder={entityIds?.length > 0 ? '' : placeholder}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <InputAdornment position="end" sx={{ padding: '10px' }}>
                                            {showCloseItemEdit && (
                                                <>
                                                    <Tooltip title="Submit">
                                                        <IconButton
                                                            onClick={(_) => handleItemTagEdit()}
                                                            aria-label="submit"
                                                            size="small"
                                                        >
                                                            <MdCheck fontSize="18" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Clear">
                                                        <IconButton
                                                            onClick={(_) => cancelItemTagEdit()}
                                                            aria-label="cancel"
                                                            size="small"
                                                        >
                                                            <MdDelete fontSize="18" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {enableCache && (
                                                <Tooltip title={refreshCacheTitle}>
                                                    <IconButton
                                                        onClick={onRefreshIconClick}
                                                        aria-label={refreshCacheTitle}
                                                        sx={{}}
                                                        size="small"
                                                    >
                                                        <MdRefresh
                                                            className="react-icon"
                                                            style={{
                                                                fontSize: '18px',
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </InputAdornment>
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            inputProps={{
                                ...params.inputProps,
                                onKeyDown: (e) => {
                                    var isNew = e.target.value !== '' && isNewValue(e.target.value);

                                    if (e.key === 'Enter' && isNew) {
                                        // Have to prevent dialog from submitting
                                        e.preventDefault();
                                    }
                                },

                                onKeyUp: (e) => {
                                    if (e.key === 'Escape') {
                                        e.preventDefault();
                                        cancelItemTagEdit();
                                    }
                                },
                            }}
                        />
                    )}
                    filterOptions={(options, params) => {
                        return filterOption(options, params);
                    }}
                    freeSolo={freeSolo}
                    PaperComponent={multiSelection ? MultiSelectDoneButton : Paper}
                />
            </div>

            <Dialog
                open={manageDialog?.isDialogOpen}
                onClose={() => {
                    dispatch(resetGroupState());
                    setManageDialog({ ...manageDialog, isDialogOpen: false });
                }}
            >
                <DialogTitle>Add a new {creatableDialogTitle}</DialogTitle>
                <DialogContent sx={{ width: '400px' }}>
                    <DialogContentText marginBottom={'5px'}>
                        Add a new item to available {creatableDialogTitle} list.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        autoFocus
                        margin="dense"
                        id="name"
                        value={manageDialog[nameProperty]}
                        onChange={(event) => {
                            validateField(event.target.value);
                            setManageDialog({
                                ...manageDialog,
                                [nameProperty]: event.target.value,
                                keyProperty: keyProperty,
                            });
                        }}
                        label={creatableDialogFieldLabel}
                        type="text"
                        variant="outlined"
                        error={!!errors}
                        helperText={errors}
                        onKeyUp={handleDialogKeyUp}
                    />
                    {creatableDialogTitle === 'category' && (
                        <FormTextField
                            component={CheckboxWithLabel}
                            Label={{
                                label: `Limit category to ${
                                    creatableParams?.asset_type_id === AssetTypes.Procedure ? 'Procedure' : 'Asset'
                                } only`,
                            }}
                            type="checkbox"
                            checked={limitCategory}
                            onChange={(e) => {
                                setLimitCategory(e.target.checked);
                            }}
                            onKeyUp={handleDialogKeyUp}
                        />
                    )}
                    {creatableDialogTitle === 'tag' && (
                        <SelectionAutocomplete
                            title="Groups"
                            placeholder="None"
                            keyProperty="group_id"
                            nameProperty="display_name"
                            entityIds={manageDialog.group_ids}
                            setEntityIds={(f, v) => setManageDialog({ ...manageDialog, group_ids: v })}
                            entitySelector={selectAllGroups}
                            entityStatusSelector={getGroupStatus}
                            fetchEntityPage={fetchGroups}
                            formField="groups"
                            fetchParams={{ page: 1, pageSize: 100 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        className={formClasses.cancel}
                        onClick={() => {
                            setManageDialog({
                                ...manageDialog,
                                isDialogOpen: false,
                            });
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={() => !errors && handleSubmit()} className={formClasses.submit} type="button">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { SelectionAutocomplete };
